import { useMemo, useRef, useState } from "react";
import Drawer from "../../../../../components/SideDrawer/SideDrawer";
import { UserDetailsStep } from "./components/UserDetailsStep";
import { FormActions } from "./components/FormActions";
import { UserDetails } from "./components/StepperForm/UserDetails/UserDetails";
import { AssignRole } from "./components/StepperForm/AssignRole/AssignRole";
import {
  AgentDetails,
  AgentDetailsRef,
} from "./components/StepperForm/AgentDetails/AgentDetails";
import { useFormik } from "formik";
import {
  AgentDetailsValues,
  AssignRoleValues,
  CreateUserError,
  CreateUserPayload,
  UserDetailsValues,
} from "./utils/types";
import {
  getUserDetailsInitialValues,
  userDetailsValidationSchema,
  assignRoleValidationSchema,
  assignRoleInitialValues,
  agentDetailsInitialValues,
  agentDetailsValidationSchema,
} from "./utils/constants";
import { useCreateUserMutation } from "../../../../../redux/services/userManagementApi";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { UserCreatedPreview } from "./components/UserCreatedPreview/UserCreatedPreview";
import { UserCreationLoader } from "./components/UserCreationLoader";
import { customToast } from "../../../../../utils/custom-toast/custom-toast";
import { clearDrawer } from "../../../../../redux/slices/userManagementSlice";
import { UserCreationErrorView } from "./components/UserCreationErrorView";
import { ErrorLogs } from "../../../utils/types";
import { UnsavedChangesModal } from "./components/UnsavedChangesModal";
import { trackEvents } from "../../../../../utils/amplitude/amplitude";

export const CreateUserDrawer = () => {
  const auth = useSelector((state: RootState) => state?.auth);
  const drawer = useSelector(
    (state: RootState) => state?.userManagement?.drawer,
  );

  const [createUser, { isLoading: isCreateUserLoading }] =
    useCreateUserMutation();

  const [createUserStep, setCreateUserStep] = useState(1);
  const [filledStep, setFilledStep] = useState(0);
  const [createdUser, setCreatedUser] = useState<CreateUserPayload | null>(
    null,
  );
  const [createUserError, setCreateUserError] = useState<CreateUserError>({
    error_logs: null,
    data: null,
  });
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const agentDetailsRef = useRef<AgentDetailsRef>(null);

  const dispatch = useDispatch();
  const orgId = useSelector((state: RootState) => state?.auth?.client?.client_code) || "unknown";
  const userId = useSelector((state: RootState) => state?.auth?.user?._id) || "unknown";

  const handleCreateUser = async () => {
    let payload: CreateUserPayload = {};
    try {
      const {
        name,
        email,
        phoneCountryCode,
        phoneNumber,
        organizations,
        isInternationalAgent,
      } = userDetailsFormik.values;

      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        selected_lead_ranges,
        utm_source_whitelist,
        utm_source_blacklist,
        utm_medium_whitelist,
        utm_medium_blacklist,
        ...agentDetails
      } = agentDetailsFormik.values;

      const appendClientCode = <K extends "utm_source" | "utm_medium">(
        data: string[],
        key: K,
      ): Array<{ client_code: string } & Record<K, string>> => {
        return data?.map((item) => ({
          client_code: auth?.client?.client_code || "",
          [key]: item,
        })) as Array<{ client_code: string } & Record<K, string>>;
      };

      const utmSourceWhitelist = appendClientCode(
        utm_source_whitelist,
        "utm_source",
      );
      const utmSourceBlacklist = appendClientCode(
        utm_source_blacklist,
        "utm_source",
      );
      const utmMediumWhitelist = appendClientCode(
        utm_medium_whitelist,
        "utm_medium",
      );
      const utmMediumBlacklist = appendClientCode(
        utm_medium_blacklist,
        "utm_medium",
      );

      payload = {
        name,
        email,
        country_code: phoneCountryCode?.dialCode,
        phone_number: phoneNumber,
        org_code: auth?.organization || "",
        role: assignRoleFormik?.values?.role || "",
        property_ids: assignRoleFormik?.values?.property_ids || [],
        demographics: {
          country_ids: (assignRoleFormik?.values?.country_ids || []).map(Number),
          city_ids: (assignRoleFormik?.values?.city_ids || []).map(Number),
        },
        clients: organizations || [],
        is_agent: isAgent,
        is_international_agent: isInternationalAgent,
        ...(isAgent && {
          ...agentDetails,
          utm_source_whitelist: utmSourceWhitelist,
          utm_source_blacklist: utmSourceBlacklist,
          utm_medium_whitelist: utmMediumWhitelist,
          utm_medium_blacklist: utmMediumBlacklist,
        }),
      };

      const response = await createUser(payload)?.unwrap();

      setCreatedUser(response);
      trackEvents("user_created", {
        user_name: userDetailsFormik.values.name || "",
        user_email: userDetailsFormik.values.email || "",
        role: assignRoleFormik.values.role || "",
        is_agent: isAgent,
        created_by: userId,
        org_id: orgId,
      });

      customToast(
        "User created successfully",
        "success",
        undefined,
        undefined,
        "bottom-left",
      );
    } catch (error) {
      const errorLogs = error as ErrorLogs;
      const errorMessage = errorLogs?.data?.detail || "Failed to create user";
      customToast(errorMessage, "error", undefined, undefined, "bottom-left");
      setCreateUserError({
        error_logs: errorLogs?.data?.detail || "Failed to create user",
        data: payload,
      });
    }
  };

  const handleCloseDrawer = () => {
    if (createdUser || createUserError?.error_logs) {
      confirmCloseDrawer();
    } else {
      setShowUnsavedChangesModal(true);
    }
  };

  const confirmCloseDrawer = () => {
    dispatch(clearDrawer());
    setCreateUserStep(1);
    setCreatedUser(null);
    setCreateUserError({
      error_logs: null,
      data: null,
    });
    setShowUnsavedChangesModal(false);
    userDetailsFormik.resetForm();
    assignRoleFormik.resetForm();
    agentDetailsFormik.resetForm();
  };

  const handleContinue = async () => {
    if (createUserStep === 1) {
      await handleUserDetailsSubmit();
    } else if (createUserStep === 2) {
      await handleAssignRoleSubmit();
    } else if (createUserStep === 3) {
      await handleAgentDetailsSubmit();
    }
  };

  const handleStepBack = (step: number) => {
    setCreateUserStep(step);
    setFilledStep(step - 1);
  };

  const handleUserDetailsSubmit = async () => {
    const errors = await userDetailsFormik.validateForm();
    userDetailsFormik.setTouched({
      name: true,
      email: true,
      phoneCountryCode: {},
      phoneNumber: true,
      organizations: true,
    });

    if (Object?.keys(errors)?.length === 0) {
      setCreateUserStep(2);
      setFilledStep(1);
    }
  };

  const handleAssignRoleSubmit = async () => {
    const errors = await assignRoleFormik.validateForm();
    assignRoleFormik.setTouched({
      role: true,
    });

    if (Object?.keys(errors)?.length !== 0) {
      return;
    }
    if (isAgent) {
      setCreateUserStep(3);
      setFilledStep(2);
    } else {
      await handleCreateUser();
    }
  };

  const handleAgentDetailsSubmit = async () => {
    const errors = await agentDetailsFormik.validateForm();
    agentDetailsFormik.setTouched({
      velocity: true,
      start_shift: true,
      end_shift: true,
      daily_assignment_limit: true,
      selected_lead_ranges: true,
    });

    if (Object?.keys(errors)?.length === 0) {
      await handleCreateUser();
    } else {
      agentDetailsRef.current?.scrollToFirstError();
    }
  };

  const handleErrorBack = () => {
    setCreateUserError({
      error_logs: null,
      data: null,
    });
  };

  const userDetailsFormik = useFormik<UserDetailsValues>({
    initialValues: getUserDetailsInitialValues(auth?.client?.region),
    validationSchema: userDetailsValidationSchema,
    enableReinitialize: true,
    onSubmit: handleUserDetailsSubmit,
  });

  const assignRoleFormik = useFormik<AssignRoleValues>({
    initialValues: assignRoleInitialValues,
    validationSchema: assignRoleValidationSchema,
    enableReinitialize: true,
    onSubmit: handleAssignRoleSubmit,
  });

  const agentDetailsFormik = useFormik<AgentDetailsValues>({
    initialValues: agentDetailsInitialValues,
    validationSchema: agentDetailsValidationSchema,
    enableReinitialize: true,
    onSubmit: handleAgentDetailsSubmit,
  });

  const isAgent = useMemo(
    () => userDetailsFormik?.values?.isAgent,
    [userDetailsFormik?.values?.isAgent],
  );

  return (
    <>
      <Drawer
        open={drawer?.open && drawer?.type === "create_user"}
        onClose={handleCloseDrawer}
        direction="right"
        size="500px"
        overlayColor="rgba(0,0,0,0.7)"
        duration={300}
        closeOnOverlayClick={true}
        closeOnEscape={true}
        drawerContainerClassName="rounded-l-[8px]"
      >
        <div className="relative h-full">
          {isCreateUserLoading ? (
            <UserCreationLoader handleCloseDrawer={confirmCloseDrawer} />
          ) : createdUser ? (
            <UserCreatedPreview
              handleCloseDrawer={confirmCloseDrawer}
              createdUser={createdUser}
            />
          ) : createUserError?.error_logs ? (
            <UserCreationErrorView
              handleCloseDrawer={confirmCloseDrawer}
              createUserError={createUserError}
              handleBack={handleErrorBack}
            />
          ) : (
            <section>
              <div className="px-[18px] pt-[18px]">
                <UserDetailsStep
                  createUserStep={createUserStep}
                  filledStep={filledStep}
                  handleCloseDrawer={handleCloseDrawer}
                  isAgent={isAgent}
                  handleStepBack={handleStepBack}
                />
                {createUserStep === 1 && (
                  <UserDetails formik={userDetailsFormik} />
                )}
                {createUserStep === 2 && (
                  <AssignRole
                    setCreateUserStep={setCreateUserStep}
                    name={userDetailsFormik?.values?.name || "User"}
                    formik={assignRoleFormik}
                  />
                )}
                {createUserStep === 3 && (
                  <AgentDetails
                    ref={agentDetailsRef}
                    setCreateUserStep={setCreateUserStep}
                    formik={agentDetailsFormik}
                  />
                )}
              </div>
              <FormActions
                onCancel={handleCloseDrawer}
                onContinue={handleContinue}
                currentStep={createUserStep}
                isAgent={isAgent}
              />
            </section>
          )}
        </div>
      </Drawer>

      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        onClose={() => setShowUnsavedChangesModal(false)}
        onSave={() => setShowUnsavedChangesModal(false)}
        onLeaveWithoutSaving={confirmCloseDrawer}
      />
    </>
  );
};
